import { Sheet } from '@silk-hq/components';
import React from 'react';
import { cn } from '../../lib/classNameHelpers';

// ================================================================================================
// Root
// ================================================================================================

type SheetRootProps = React.ComponentPropsWithoutRef<typeof Sheet.Root>;
type BottomSheetRootProps = Omit<SheetRootProps, 'license'> & {
  license?: SheetRootProps['license'];
};

function BottomSheetRoot({ children, ...restProps }: BottomSheetRootProps) {
  return (
    <Sheet.Root license="commercial" {...restProps}>
      {children}
    </Sheet.Root>
  );
}

// ================================================================================================
// View
// ================================================================================================

function BottomSheetView({
  children,
  className,
  ...restProps
}: React.ComponentPropsWithoutRef<typeof Sheet.View>) {
  return (
    <Sheet.View
      className={cn('z-[1] h-[calc(var(--silk-100-lvh-dvh-pct)+60px)]', className)}
      nativeEdgeSwipePrevention={true}
      {...restProps}
    >
      {children}
    </Sheet.View>
  );
}

// ================================================================================================
// Backdrop
// ================================================================================================

function BottomSheetBackdrop({
  className,
  ...restProps
}: React.ComponentPropsWithoutRef<typeof Sheet.Backdrop>) {
  return (
    <Sheet.Backdrop
      className={cn('BottomSheet-backdrop', className)}
      themeColorDimming="auto"
      {...restProps}
    />
  );
}

// ================================================================================================
// Content
// ================================================================================================

function BottomSheetContent({
  children,
  className,
  ...restProps
}: React.ComponentPropsWithoutRef<typeof Sheet.Content>) {
  return (
    <Sheet.Content className={cn('box-border h-auto min-h-[100px]', className)} {...restProps}>
      <Sheet.BleedingBackground className="rounded-3xl bg-backgroundPrimary shadow-lg" />
      {children}
    </Sheet.Content>
  );
}

// ================================================================================================
// Handle
// ================================================================================================

function BottomSheetHandle({
  className,
  ...restProps
}: React.ComponentPropsWithoutRef<typeof Sheet.Handle>) {
  return (
    <Sheet.Handle
      className={cn('w-[50px] h-1.5 border-0 rounded-full bg-gray-300 cursor-pointer', className)}
      action="dismiss"
      {...restProps}
    />
  );
}

// ================================================================================================
// Individual Exports (for Fast Refresh compatibility)
// ================================================================================================

export {
  BottomSheetRoot,
  BottomSheetView,
  BottomSheetBackdrop,
  BottomSheetContent,
  BottomSheetHandle,
};
export const BottomSheetPortal = Sheet.Portal;
export const BottomSheetTrigger = Sheet.Trigger;
export const BottomSheetOutlet = Sheet.Outlet;
export const BottomSheetTitle = Sheet.Title;
export const BottomSheetDescription = Sheet.Description;
